import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

// Scroll motion for the public pages.
//
// Hand-rolled on IntersectionObserver rather than pulled from a library:
// the whole vocabulary is "fade up when it arrives" and "which of these is
// on screen", which is about forty lines, and a motion library would be a
// bigger download than the page it is animating.
//
// Everything here is off when the visitor has asked for less motion. That
// is not a nicety — for some people this kind of movement is the
// difference between reading a page and closing it.

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return !!reduced;
}

/** True once this element has been scrolled into view. Never goes back. */
export function useInView<T extends HTMLElement>(rootMargin = '-12% 0px -12% 0px') {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    // jsdom has no IntersectionObserver; without this the tests render a
    // page whose every section is invisible.
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setSeen(true); observer.disconnect(); } },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return { ref, seen };
}

/**
 * Fades and lifts its children in when they arrive. `delay` staggers a row
 * of them; keep it under about 250ms or the last card feels broken rather
 * than choreographed.
 */
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  /** How far it travels, px. Small — this is punctuation, not a slideshow. */
  distance?: number;
  sx?: SxProps<Theme>;
}> = ({ children, delay = 0, distance = 18, sx }) => {
  const reduced = usePrefersReducedMotion();
  const { ref, seen } = useInView<HTMLDivElement>();
  const on = seen || reduced;

  return (
    <Box
      ref={ref}
      sx={{
        opacity: on ? 1 : 0,
        transform: on ? 'none' : `translate3d(0, ${distance}px, 0)`,
        transition: reduced
          ? 'none'
          : `opacity 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms,
             transform 620ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: on ? 'auto' : 'opacity, transform',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Steps through 0..count-1 on a timer, pausing while the section is off
 * screen and stopping entirely under reduced motion — an animation nobody
 * is looking at is just battery.
 */
export function useLoop(count: number, intervalMs: number, active = true): number {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduced || !active || count < 2) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % count), intervalMs);
    return () => window.clearInterval(timer);
  }, [count, intervalMs, active, reduced]);

  // Under reduced motion the last frame is the informative one: the whole
  // story has happened rather than none of it.
  return reduced ? count - 1 : index;
}

/**
 * Progress through a pinned scene: a tall container whose inner content is
 * `position: sticky`. Returns which of `count` beats the scroll is on.
 *
 * This replaced an IntersectionObserver over the step text. That approach
 * switched the visual when a step crossed the middle of the window, but the
 * text kept scrolling while the pinned visual stayed put — so the two
 * halves were only ever level for one instant and looked broken the rest of
 * the time. Pinning both and driving them from one number keeps them
 * locked together.
 */
export function useScene<T extends HTMLElement>(count: number): {
  ref: React.RefObject<T | null>;
  index: number;
  /** Fractional position, 0 .. count-1. Drives motion between beats. */
  progress: number;
} {
  const ref = useRef<T | null>(null);
  const [state, setState] = useState({ index: 0, progress: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return undefined;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      // How far the scene has travelled, over the distance it can travel.
      const travelled = -rect.top;
      const distance = el.offsetHeight - window.innerHeight;
      if (distance <= 0) return;
      const t = Math.min(Math.max(travelled / distance, 0), 0.999);
      const progress = t * count - 0.5;
      setState({
        index: Math.min(count - 1, Math.floor(t * count)),
        progress: Math.min(Math.max(progress, 0), count - 1),
      });
    };

    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [count]);

  return { ref, ...state };
}

/** Which of several sections is currently the one being read. */
export function useActiveSection(count: number): {
  setRef: (index: number) => (el: HTMLElement | null) => void;
  active: number;
} {
  const elements = useRef<(HTMLElement | null)[]>([]);
  const [active, setActive] = useState(0);

  const setRef = useMemo(
    () => (index: number) => (el: HTMLElement | null) => { elements.current[index] = el; },
    [],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = elements.current.indexOf(entry.target as HTMLElement);
          if (index >= 0) setActive(index);
        });
      },
      // A band across the middle of the window, so a section becomes
      // "current" when it is where your eyes are, not when it first peeks
      // in at the bottom.
      { rootMargin: '-45% 0px -45% 0px' },
    );
    elements.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [count]);

  return { setRef, active };
}
